import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, signal, } from '@angular/core';
import { ChartBase } from '../base/base.chart';
import { ChartData, ChartJsType, ChartOptions, } from '../../models/chart.models';
import { Chart, ChartDataset } from 'chart.js';
import { ChartLegendComponent } from '../../plugins/legend/legend.component';
import { htmlLegendPlugin } from '../../plugins/legend/legend.plugins';
import {
  decimateDatasetsByTime,
  DecimationMode,
  toTimestamp,
  VisibleRange,
} from '../../../app/core/utils/decimation.util';

/**
 * ChartBarComponent
 *
 * A responsive Angular component for rendering a bar chart.
 * Uses Chart.js for visualization and supports configurable datasets,
 * colors, and annotations. The component is fully responsive.
 *
 * Con escala de tiempo, solo se dibujan los puntos de la ventana visible (zoom/pan) y, si son
 * demasiados, se agrupan en intervalos de tiempo comunes a todas las series conservando picos y valles.
 */
@Component({
  selector: 'chart-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChartBase, ChartLegendComponent],
  template: `
    <div class="flex flex-col gap-y-4 w-full">
      <chart-legend
        #legendRef
        [containerID]="'chart-legend'"
        [label]="legendLabel()"
        (ready)="setLegend(legendRef)"
      />
      <chart-base
        [type]="type()"
        [data]="data()"
        [height]="height()"
        [options]="optionsComputed()"
        [plugins]="plugins()"
      />
    </div>
  `,
})
export class ChartBarComponent {
  showCustomLegend = input<boolean>(false);

  /**
   * Signal to store the HtmlLegendComponent instance.
   */
  legendComponent = signal<ChartLegendComponent | null>(null);

  /**
   * Signal to store the HtmlLegendComponent instance.
   */
  legendLabel = input<string | null>(null);

  /**
   * `datasets`: Required input defining the data values of the bar chart.
   */
  datasets = input<ChartDataset<'bar', (number | [number, number] | null)[]>[]>(
    [],
  );

  /**
   * `numPoints`: Número máximo (aproximado) de puntos que se dibujan a la vez en escala de tiempo.
   */
  numPoints = input<number>(10);

  /**
   * `decimationMode`: cómo se reducen los datos cuando hay más de `numPoints`.
   * - `min-max`: valle y pico de cada intervalo (por defecto).
   * - `peaks`: solo el pico de cada intervalo.
   */
  decimationMode = input<DecimationMode>('min-max');

  /**
   * `labels`: Required input defining the labels values of the bar chart.
   */
  labels = input<string[]>([]);

  /**
   * `color`: Defines the color used for the bars. Defaults to `#6093F4`.
   */
  color = input<string>('#6093F4');

  /**
   * `height`: Defines the height for the chart. Defaults to `280px`.
   */
  height = input<number>(420);

  /**
   * `units`: Defines the units for the chart. Defaults to `''`.
   */
  units = input<string>('');

  /**
   * `annotations`: Optional input specifying annotations for the chart.
   */
  annotations = input<any>();

  /**
   * `options`: Optional input to provide additional configuration options.
   */
  options = input<ChartOptions<'bar'>>();

  /**
   * Ventana de zoom/pan elegida por el usuario. Solo la actualizan los gestos de zoom y pan.
   * Vuelve a `null` cada vez que se generan opciones nuevas (cambian los datasets, las unidades...),
   * porque en ese momento el gráfico también pierde el zoom.
   *
   * No se rellena a partir de los límites del eje: ese eje se ajusta a los datos ya decimados
   * y eso retroalimentaba la decimación sin estabilizarse nunca.
   */
  private readonly zoomRange = linkedSignal<VisibleRange | null>(
    () => {
      this.optionsComputed();
      return null;
    },
    { equal: (a, b) => a?.min === b?.min && a?.max === b?.max },
  );

  /** Primer y último instante de todos los datasets. */
  private readonly dataExtent = computed(() =>
    this.getMinMaxFromDatasets(this.datasets()),
  );

  /** Ventana fijada por el padre con `options.scales.x.min/max`, si la hay. */
  private readonly parentRange = computed<VisibleRange | null>(() => {
    const xScale = this.options()?.scales?.['x'];
    const min = toTimestamp(xScale?.min);
    const max = toTimestamp(xScale?.max);
    const hasMin = Number.isFinite(min);
    const hasMax = Number.isFinite(max);
    if (!hasMin && !hasMax) {
      return null;
    }
    const extent = this.dataExtent();
    return { min: hasMin ? min : extent.min, max: hasMax ? max : extent.max };
  });

  /**
   * Rango con el que se filtran los datos: el zoom del usuario o, si no hay, la ventana del padre.
   * Se descarta si no se solapa con los datos actuales. `null` equivale a "todo el rango".
   */
  private readonly effectiveRange = computed(() => {
    const range = this.zoomRange() ?? this.parentRange();
    const { min, max } = this.dataExtent();
    return range && range.max >= min && range.min <= max ? range : null;
  });

  /** `true` solo si el padre pide explícitamente `type: 'time'` en el eje X. */
  private readonly isTimeScale = computed(
    () => this.options()?.scales?.['x']?.type === 'time',
  );

  /**
   * Se decima cuando el eje X es de tiempo y los datos son puntos `{ x, y }`.
   * `optionsComputed` aplica el eje de tiempo siempre que el tipo no sea 'category', aunque el padre
   * no lo indique, por eso aquí también se acepta un tipo sin definir.
   */
  private readonly shouldDecimate = computed(() => {
    const type = this.options()?.scales?.['x']?.type;
    const isTimeAxis =
      type === undefined || type === 'time' || type === 'timeseries';
    const hasXYPoints = this.datasets().some((dataset) => {
      const first = dataset.data.find(
        (point) => point !== null && point !== undefined,
      );
      return typeof first === 'object' && first !== null && 'x' in first;
    });
    return isTimeAxis && hasXYPoints;
  });

  /**
   * Datasets ordenados por `x`. Solo depende de los datasets, así que no se reordena en cada zoom.
   */
  private readonly sortedDatasets = computed(() =>
    this.datasets().map((dataset) => ({
      ...dataset,
      data: [...dataset.data].sort(
        (a, b) => toTimestamp((a as any)?.x) - toTimestamp((b as any)?.x),
      ),
    })),
  );

  /**
   * Computed `data` property defining the structure of the bar chart.
   * En escala de tiempo: filtra por la ventana visible y reduce conservando picos y valles.
   */
  data = computed<ChartData<'bar'>>(() => {
    if (!this.shouldDecimate()) {
      return {
        labels: this.labels(),
        datasets: this.datasets().map((dataset) => ({ ...dataset })),
      };
    }

    return {
      labels: undefined,
      datasets: decimateDatasetsByTime(
        this.sortedDatasets(),
        this.numPoints(),
        this.effectiveRange(),
        this.decimationMode(),
      ) as ChartData<'bar'>['datasets'],
    };
  });

  /**
   * Computed `optionsComputed` property configuring responsiveness,
   * grid display, and scaling for the y-axis. Supports optional annotations.
   */
  optionsComputed = computed<ChartOptions<'bar'>>(() => {
    const { min, max } = this.dataExtent();
    const units = this.units();
    return {
      responsive: true,
      maintainAspectRatio: false,
      parsing: this.isTimeScale() ? false : (true as any),
      animation: false,
      spanGaps: true,
      plugins: {
        htmlLegend: {
          containerID: 'chart-legend',
          legendComponent: this.legendComponent(),
        },
        legend: {
          display: false, // Hides legend.
        },
        zoom: {
          limits: {
            x: {
              min: min,
              max: max,
            },
          },
          pan: {
            onPan: ({ chart }) => this.updateChartFn({ chart }),
            enabled: true,
            mode: 'x', // Enable horizontal panning
          },
          zoom: {
            onZoom: ({ chart }) => this.updateChartFn({ chart }),
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x', // Enable horizontal zooming
          },
          ...this.options()?.plugins?.['zoom'],
        },
        annotation: {
          annotations: this.annotations(),
        },
        tooltip: {
          callbacks: {
            // Custom callback for the label
            label: (context) => {
              return `${context?.formattedValue} ${this.units()}`; // Display the custom label
            },
          },
        },
      },
      scales: {
        x: {
          // Only apply time-specific min/max for time scales
          ...(this.isTimeScale() && { min, max }),
          // Only apply time configuration if type is not explicitly set to 'category'
          ...(this.options()?.scales?.['x']?.type !== 'category' && {
            type: 'time',
            time: {
              displayFormats: {
                minute: 'MMM d, yyyy HH:mm',
                hour: 'MMM d, yyyy HH:mm',
                day: 'MMM d, yyyy',
                week: 'MMM d, yyyy',
                month: 'MMM yyyy',
                quarter: '[Q]Q yyyy',
                year: 'yyyy',
              },
              tooltipFormat: 'MM d, HH:mm', // Tooltip format
            },
          }),
          grid: {
            display: false, // Removes x-axis gridlines.
          },
          ticks: {
            source: 'auto',
            // Disabled rotation for performance
            maxRotation: 45,
            autoSkip: true,
          },
          // User options override everything
          ...(this.options()?.scales?.['x'] as any),
        },

        y: {
          grid: {
            display: true, // Enables y-axis gridlines.
          },
          border: {
            dash: (context) =>
              context.index % 2 === 0 ? [Number.POSITIVE_INFINITY] : [3, 3],
          },
          ticks: {
            callback: function (value) {
              const num = typeof value === 'number' ? value : Number(value);
              const formatted = Number.isFinite(num)
                ? new Intl.NumberFormat(undefined, {
                    maximumFractionDigits: 2,
                  }).format(num)
                : String(value);
              return units ? `${formatted} ${units}` : formatted;
            },
          },
          ...this.options()?.scales?.['y'],
        },
      },
    };
  });

  /**
   * `type`: Specifies the chart type as `bar`.
   */
  type = signal<ChartJsType>('bar');

  /**
   * `plugins`: Defines any additional Chart.js plugins.
   */
  plugins = computed(() => (this.showCustomLegend() ? [htmlLegendPlugin] : []));

  /**
   * Set the LegendComponent reference to the signal.
   */
  setLegend(legendComponent: ChartLegendComponent) {
    this.legendComponent.set(legendComponent);
  }

  /**
   * Lee el rango visible del eje X tras un zoom o pan y lo guarda en el signal.
   * `data` se recalcula solo a partir de ese valor.
   */
  updateChartFn = ({ chart }: { chart: Chart }) => {
    const xScale = chart.scales['x'];
    this.setVisibleRange(xScale.min, xScale.max);
  };

  private setVisibleRange(min: number, max: number): void {
    if (Number.isFinite(min) && Number.isFinite(max)) {
      this.zoomRange.set({ min, max });
    }
  }

  /**
   * Get the minimum and maximum values from a Chart.js dataset array.
   * @param datasets - Array of ChartDataset<'line'> objects.
   * @returns An object with min and max values for the dataset.
   */
  getMinMaxFromDatasets(
    datasets: ChartDataset<'bar', (number | [number, number] | null)[]>[],
  ): {
    min: number;
    max: number;
  } {
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;

    datasets.forEach((dataset) => {
      dataset.data.forEach((point) => {
        if (typeof point === 'object' && 'x' in point!) {
          const xValue =
            typeof point.x === 'number'
              ? point.x
              : new Date((point as any).x).getTime();
          minX = Math.min(minX, xValue);
          maxX = Math.max(maxX, xValue);
        }
      });
    });

    return { min: minX, max: maxX };
  }
}
